import React, { useRef, useEffect } from 'react';

export default function QuizView({
  // Loading states
  isLoading,
  isSubmitting,
  error,

  // Session data
  question,
  difficulty,

  // Conversation
  messages,

  // Streaming reply (grading via SSE)
  streamingReply,
  isStreaming,

  // Completion state
  isPassed,
  score,

  // User input
  inputValue,
  setInputValue,

  // Actions
  submitAnswer,
  retry,

  // Refs
  chatEndRef,

  // Navigation
  onSelectNavNext,
  hasNextSection,
}) {
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current && messages?.length > 0) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-grow the answer textarea to fit its content, capped at 120px (then
  // it scrolls internally). Keyed on inputValue (not a DOM event handler)
  // so it fires both on user typing AND when submitAnswer programmatically
  // clears inputValue — resetting the height back to single-line instead of
  // staying stuck large after a long submitted answer.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [inputValue]);

  const canSend = !isLoading && !isSubmitting && inputValue.trim() && !isPassed;

  // LOADING STATE
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid var(--border-subtle)',
            borderTopColor: 'var(--accent-primary)',
            borderRadius: '50%',
            animation: 'graspd-spin 0.8s linear infinite',
          }}
        />
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-panel-size)' }}>
          Loading quiz...
        </p>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--danger-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--danger)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-panel-size)', fontWeight: 500 }}>
          {error}
        </p>
        <button
          type="button"
          onClick={retry}
          style={{
            padding: '10px 20px',
            fontSize: 'var(--text-body-size)',
            fontWeight: 500,
            borderRadius: 'var(--radius-control)',
            background: 'var(--accent-primary)',
            color: 'var(--text-inverted)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes quizCaretBlink {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }
        @keyframes quizThinkingDot {
          0%, 80%, 100% { opacity: 0.35; transform: scale(0.72); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      {/* HEADER: Question and difficulty */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 24px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-secondary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-label-size)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--accent-primary)',
              background: 'var(--accent-light)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-control)',
            }}
          >
            {difficulty || 'Quiz'}
          </span>
          {isPassed && (
            <span
              style={{
                fontSize: 'var(--text-label-size)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--success)',
                background: 'var(--success-bg)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-control)',
              }}
            >
              Passed
            </span>
          )}
          {score !== null && (
            <span
              style={{
                fontSize: 'var(--text-label-size)',
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                marginLeft: 'auto',
              }}
            >
              Score: {Math.round(score * 100)}%
            </span>
          )}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-panel-size)',
            color: 'var(--text-primary)',
            lineHeight: 1.5,
          }}
        >
          {question}
        </p>
      </div>


      {/* MESSAGES: Always show conversation history */}
      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginTop: 'auto',
            width: '100%',
            maxWidth: 'var(--frame-prose)',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {(messages || []).map((msg, index) => (
            <div
              key={`${msg.timestamp}-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 4,
              }}
            >
              <div
                style={{
                  maxWidth: '70%',
                  background:
                    msg.role === 'user'
                      ? 'var(--accent-primary)'
                      : 'var(--glass-bg-medium)',
                  color:
                    msg.role === 'user'
                      ? 'var(--text-inverted)'
                      : 'var(--text-primary)',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-overlay)',
                  fontSize: 'var(--text-panel-size)',
                  lineHeight: 1.5,
                  boxShadow:
                    msg.role === 'user' ? 'var(--shadow-sm)' : 'none',
                }}
              >
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
              </div>
              <div
                style={{
                  fontSize: 'var(--text-label-size)',
                  color: 'var(--text-tertiary)',
                  opacity: 0.5,
                  paddingLeft: msg.role === 'user' ? 0 : 16,
                  paddingRight: msg.role === 'user' ? 16 : 0,
                }}
              >
                {new Date(msg.timestamp || Date.now()).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          ))}

          {/* Provisional assistant bubble while the grading reply streams in.
              Mirrors the assistant-message markup above exactly so the swap
              to the real transcript entry on `complete` causes no visual
              jump. Before the first character is revealed, show a staggered
              "thinking" dot pulse instead of an empty bubble with a lone
              caret. */}
          {isStreaming && (
            <div
              data-testid="quiz-streaming-bubble"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 4,
              }}
            >
              <div
                style={{
                  maxWidth: '70%',
                  background: 'var(--glass-bg-medium)',
                  color: 'var(--text-primary)',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-overlay)',
                  fontSize: 'var(--text-panel-size)',
                  lineHeight: 1.5,
                  boxShadow: 'none',
                }}
              >
                {streamingReply ? (
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {streamingReply}
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1em',
                        marginLeft: '2px',
                        verticalAlign: 'text-bottom',
                        background: 'var(--accent-primary)',
                        animation: 'quizCaretBlink 1s steps(1) infinite',
                      }}
                    />
                  </p>
                ) : (
                  <div
                    aria-label="Thinking"
                    role="status"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 2px',
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        aria-hidden="true"
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: 'var(--accent-primary)',
                          animation: 'quizThinkingDot 1.4s ease-in-out infinite',
                          animationDelay: `${i * 0.16}s`,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* BOTTOM AREA: Input or completion banner */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {isPassed ? (
          /* COMPLETION BANNER */
          <div
            style={{
              padding: '14px 20px',
              background: 'var(--bg-elevated)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: 'var(--radius-panel)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid var(--border-subtle)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--success)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--text-inverted)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span
                style={{
                  fontSize: 'var(--text-panel-size)',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}
              >
                Quiz Completed!
              </span>
            </div>
            {hasNextSection && (
              <button
                type="button"
                onClick={onSelectNavNext}
                style={{
                  height: 36,
                  padding: '0 16px',
                  fontSize: 'var(--text-body-size)',
                  fontWeight: 500,
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--btn-primary-bg)',
                  color: 'var(--btn-primary-text)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'opacity 0.15s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = '0.85';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <span>Continue</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          /* INPUT AREA */
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div
              style={{
                flex: 1,
                borderRadius: 'var(--radius-overlay)',
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <textarea
                ref={textareaRef}
                placeholder="Type your answer..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (canSend) {
                      submitAnswer();
                    }
                  }
                }}
                onPaste={(e) => {
                  // Disable paste in production to encourage authentic learning
                  if (process.env.NODE_ENV === 'production') {
                    e.preventDefault();
                  }
                }}
                disabled={isSubmitting}
                rows={1}
                style={{
                  width: '100%',
                  padding: '12px 18px',
                  fontSize: 'var(--text-panel-size)',
                  lineHeight: 1.5,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-overlay)',
                  color: 'var(--text-primary)',
                  resize: 'none',
                  overflowY: 'auto',
                  fontFamily: 'inherit',
                  outline: 'none',
                  minHeight: 48,
                  maxHeight: 120,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <button
              type="button"
              onClick={submitAnswer}
              disabled={!canSend}
              style={{
                height: 48,
                width: 48,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: canSend ? 'none' : '1px solid var(--border-subtle)',
                background: canSend
                  ? 'var(--btn-primary-bg)'
                  : 'var(--bg-secondary)',
                color: canSend ? 'var(--btn-primary-text)' : 'var(--text-tertiary)',
                cursor: canSend ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s, color 0.2s, transform 0.1s, opacity 0.15s',
              }}
              onMouseDown={(e) => {
                if (canSend) e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseOver={(e) => {
                if (canSend) e.currentTarget.style.opacity = '0.85';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.opacity = '1';
              }}
            >
              {isSubmitting ? (
                <div
                  style={{
                    width: 18,
                    height: 18,
                    border: '2px solid var(--bg-tertiary)',
                    borderTopColor: 'var(--bg-primary)',
                    borderRadius: '50%',
                    animation: 'graspd-spin 0.8s linear infinite',
                  }}
                />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 17L17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
